import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type WebSocketStatus = 'disconnected' | 'connecting' | 'connected';

export interface WebSocketMessage {
  channel: string;
  message: Record<string, unknown>;
}

interface WebSocketState {
  status: WebSocketStatus;
  // Resource channels the user currently wants; replayed on reconnect. Identity channels
  // (user/role/company/system) are assigned server-side at $connect and are not tracked here.
  channels: string[];
  lastMessage: WebSocketMessage | null;
}

const initialState: WebSocketState = {
  status: 'disconnected',
  channels: [],
  lastMessage: null,
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Command actions consumed by websocketMiddleware (connect/disconnect are no-ops here).
    wsConnect() {},
    wsDisconnect() {},
    wsSubscribe(state, action: PayloadAction<string>) {
      if (!state.channels.includes(action.payload)) {
        state.channels.push(action.payload);
      }
    },
    wsUnsubscribe(state, action: PayloadAction<string>) {
      state.channels = state.channels.filter((channel) => channel !== action.payload);
    },
    // Emitted by the middleware as the socket lifecycle changes.
    wsStatusChanged(state, action: PayloadAction<WebSocketStatus>) {
      state.status = action.payload;
    },
    // Seam for inbound frames. Slices will wire `extraReducers` to this action once the
    // payload contract (whole object vs delta) is finalized; for now we just stash the last
    // message so the plumbing is observable.
    wsMessageReceived(state, action: PayloadAction<WebSocketMessage>) {
      state.lastMessage = action.payload;
    },
  },
});

export const {
  wsConnect,
  wsDisconnect,
  wsSubscribe,
  wsUnsubscribe,
  wsStatusChanged,
  wsMessageReceived,
} = websocketSlice.actions;
export default websocketSlice.reducer;
